// src/data/dataUtils.js

/**
 * Format a number to 1 decimal place for display.
 * Safe: returns '0.0' for null/undefined/NaN.
 * @param {any} value
 * @returns {string}
 */
export const f1 = (value) => {
  const n = Number(value);
  return isNaN(n) ? '0.0' : n.toFixed(1);
};

/**
 * Utility function to ensure a value is a number
 * @param {any} value - The value to convert
 * @param {number} defaultValue - Default value if conversion fails
 * @returns {number} The converted number
 */
export const ensureNumber = (value, defaultValue = 0) => {
  if (value === undefined || value === null) return defaultValue;
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

/**
 * Utility function to ensure a value is a boolean
 * @param {any} value - The value to convert
 * @param {boolean} defaultValue - Default value if conversion fails
 * @returns {boolean} The converted boolean
 */
export const ensureBoolean = (value, defaultValue = false) => {
  if (value === undefined || value === null) return defaultValue;
  return Boolean(value);
};

/**
 * Utility function to transform all numeric fields in an object
 * @param {Object} obj - The object to transform
 * @param {Object} fieldMappings - Object mapping field names to transformation functions
 * @returns {Object} The transformed object
 */
export const transformObject = (obj, fieldMappings = {}) => {
  if (!obj) return {};
  
  const result = { ...obj };
  
  // Apply specific field mappings first
  Object.entries(fieldMappings).forEach(([field, transform]) => {
    if (field in result) {
      result[field] = transform(result[field]);
    }
  });
  
  // For all other fields that look numeric but aren't in the mappings,
  // try to convert them to numbers automatically
  Object.entries(result).forEach(([key, value]) => {
    if (!(key in fieldMappings) && typeof value === 'string' && !isNaN(value)) {
      result[key] = Number(value);
    }
  });
  
  return result;
};

/**
 * Gets the top N items from an array based on a field
 * @param {Array} data - Array of data
 * @param {string} field - Field to sort by
 * @param {number} count - Number of items to return
 * @param {boolean} descending - Whether to sort in descending order
 * @returns {Array} Top N items
 */
export const getTopN = (data, field, count = 3, descending = true) => {
  if (!data || !Array.isArray(data) || data.length === 0) return [];

  // Sort the entire array - modern JS engines are highly optimized for this
  // For FPL league sizes (typically < 50 managers), full sort is fast and simple
  const sorted = [...data].sort((a, b) => {
    const aValue = ensureNumber(a[field]);
    const bValue = ensureNumber(b[field]);
    return descending ? bValue - aValue : aValue - bValue;
  });

  // Return top N items with position indicator
  return sorted.slice(0, Math.min(count, sorted.length))
    .map((item, index) => ({
      ...item,
      position: index + 1
    }));
};